<template>
  <div
    :class="[
      'rounded-lg border p-4 transition-all hover:shadow-md',
      isEnabled
        ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60',
    ]"
  >
    <div class="space-y-3">
      <div class="flex items-start justify-between">
        <div class="min-w-0 flex-1">
          <h4 class="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {{ tool.name }}
          </h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
            {{ tool.description }}
          </p>
        </div>
        <span :class="typeBadgeClass">{{ typeBadgeLabel }}</span>
      </div>

      <div v-if="tool.tags && tool.tags.length > 0" class="flex flex-wrap gap-1">
        <span
          v-for="tag in tool.tags"
          :key="tag"
          class="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
        >
          {{ tag }}
        </span>
      </div>

      <div class="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
        <template v-if="isBase">
          <Switch :model-value="isEnabled" @update:model-value="emit('toggle-enable')" />
          <Button variant="outline" size="sm" @click="emit('view-detail')">Details</Button>
        </template>
        <template v-else>
          <div class="flex items-center space-x-2">
            <Button variant="outline" size="sm" @click="emit('edit')">Edit</Button>
            <Button variant="outline" size="sm" @click="emit('view-detail')">Env</Button>
            <Button variant="outline" size="sm" @click="emit('builds')">Builds</Button>
            <Button variant="outline" size="sm" @click="emit('versions')">Versions</Button>
            <Button variant="danger" size="sm" @click="emit('delete')">Delete</Button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Button, Switch } from '@asabdev/asab-ui';
  import type { BaseTool, CustomTool } from '@/lib/mcp/types';

  interface Props {
    tool: BaseTool | CustomTool;
    isBase: boolean;
    isEnabled: boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'toggle-enable'): void;
    (e: 'edit'): void;
    (e: 'delete'): void;
    (e: 'view-detail'): void;
    (e: 'builds'): void;
    (e: 'versions'): void;
  }>();

  const typeBadgeLabel = computed(() => {
    if (props.isBase) {
      return props.tool.wasm_ref ? 'wasm' : 'native';
    }
    const tags = props.tool.tags ?? [];
    if (tags.includes('rust')) return 'rust';
    if (tags.includes('typescript')) return 'typescript';
    if (tags.includes('python')) return 'python';
    return 'custom';
  });

  const typeBadgeClass = computed(() => {
    const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
    switch (typeBadgeLabel.value) {
      case 'native':
        return `${base} bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`;
      case 'wasm':
        return `${base} bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300`;
      case 'rust':
        return `${base} bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300`;
      case 'typescript':
        return `${base} bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300`;
      case 'python':
        return `${base} bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300`;
      default:
        return `${base} bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300`;
    }
  });
</script>
